import type { ClientKind } from "@hamlak/shared";
export type { ClientKind };

/** 네이티브 전용 비밀 저장소(Android @capacitor/preferences, iOS Keychain = 앱 내장 플러그인 HamlakSecureStorage). 웹 구현은 없다. */
export interface SecureStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export type ShareResult = "shared" | "copied" | "dismissed";

export interface DevicePort {
  /** 네이티브 Device.getId(), 웹은 localStorage UUID. 서버 deviceId·Amplitude deviceId에 같은 값. */
  getDeviceId(): Promise<string>;
  /** 카카오톡·인스타그램 등 임베디드 WebView(User-Agent 판정). 네이티브는 항상 false. */
  isInAppBrowser(): boolean;
  /** 클립보드 복사(메시지 복사·iOS 인앱 브라우저 URL 복사). */
  copy(text: string): Promise<void>;
  /** 기기 모델·OS 문자열(고객센터 자동 첨부). */
  getInfo(): Promise<DeviceInfo>;
  /**
   * 링크 공유(플롯 프로필 ⋯ → 공유하기, AC0-270). 네이티브 = 시스템 공유 시트(@capacitor/share), 웹 = navigator.share, 없으면 클립보드 복사.
   * shared = 공유 시트를 거쳤다, copied = 링크를 복사했다(호출부가 토스트), dismissed = 사용자가 닫았다(무동작). 그 외 실패는 reject.
   */
  share(input: { title: string; url: string }): Promise<ShareResult>;
  /** 네이티브 어댑터만 갖는다. 호출부는 clientKind !== 'web'일 때만 접근한다. */
  secureStore?: SecureStore;
}

/** 로그인 실패 사유. cancelled = 사용자 취소(무동작), unavailable = 이 환경에서 시도 불가, failed = 그 외. */
export type AuthErrorCode = "cancelled" | "unavailable" | "failed";
export type AuthError = { code: AuthErrorCode; cause?: unknown };
export const isAuthError = (e: unknown): e is AuthError =>
  typeof e === "object" && e !== null && "code" in e && typeof (e as AuthError).code === "string";

/**
 * nonce는 클라이언트가 만든 원문(필수). Apple에는 sha256(nonce)를 보냈고, 서버가 id_token 클레임과 sha256(원문)을 대조한다(재생·콜백 탈취 방지).
 * authorizationCode(일회용·5분 유효)는 iOS 네이티브·웹 팝업·Android 콜백 모두 받는 대로 돌려준다. 호출부가 POST /auth/apple 에 싣고,
 * 서버가 로그인 직후 refresh 토큰으로 교환해 두었다가 탈퇴 때 revoke 한다(AC0-269, ios.md B4). 없으면 생략(서버 계약상 선택).
 */
export type AppleSignInResult = { idToken: string; nonce: string; authorizationCode?: string };

export interface AuthPort {
  /** Google ID 토큰. 네이티브 = Credential Manager. 웹은 쓰지 않는다(startGoogleRedirect). 실패는 AuthError로 reject. */
  signInWithGoogle(): Promise<{ idToken: string }>;
  /** Apple ID 토큰. 웹 = Sign in with Apple JS 팝업, Android = Custom Tabs 웹 OAuth → hamlak://auth/apple 복귀, iOS = 네이티브 시트. 실패는 AuthError로 reject. */
  signInWithApple(): Promise<AppleSignInResult>;
  /** 웹 전용: 서버 리다이렉트 로그인 시작(페이지를 떠난다). 결과는 /login?googleHandle=…|googleError=… 로 돌아온다(auth-session 3.9). */
  startGoogleRedirect?(returnTo: string | null): void;
  signOut(): Promise<void>;
}

export interface BrowserPort {
  /** 외부 링크(약관·고객센터). 네이티브 Custom Tabs, 웹은 새 탭. */
  open(url: string): Promise<void>;
}

export type AppInfo = { version: string; build: number | null };

export interface AppPort {
  /** 포그라운드 복귀(네이티브 appStateChange isActive=true, 웹 visibilitychange visible). 해제 함수를 돌려준다. */
  onForeground(cb: () => void): () => void;
  /** 앱 버전·빌드. 네이티브 App.getInfo(), 웹은 빌드 시 주입된 __APP_VERSION__(build 없음). */
  getInfo(): Promise<AppInfo>;
  /** Android 하드웨어 뒤로가기(네이티브 backButton). 웹은 브라우저가 처리하므로 등록해도 호출되지 않는다. 해제 함수를 돌려준다. */
  onBackButton(cb: () => void): () => void;
  /** 앱 종료(Android 홈에서 뒤로가기). 웹은 no-op. */
  exit(): Promise<void>;
  /** 네이티브 스플래시(launchAutoHide:false) 숨김 + OTA 번들 정상 알림. 앱 셸이 첫 화면을 그린 뒤 부른다. 웹은 no-op. */
  hideSplash(): Promise<void>;
  /**
   * 딥링크·앱 링크로 앱이 열린 URL(AC0-261). 네이티브: 웜 스타트 appUrlOpen + 콜드 스타트 launch URL(1회, 같은 URL 중복은 한 번만).
   * 다른 흐름이 소비한 앱 스킴 URL(`hamlak://auth/…`)은 오지 않는다. 웹은 no-op(주소창이 곧 라우트). 해제 함수를 돌려준다.
   */
  onOpenUrl(cb: (url: string) => void): () => void;
}

/** 고객센터 자동 첨부용 기기·OS 문자열(1.19). 네이티브 Device.getInfo(), 웹은 User-Agent 요약. */
export type DeviceInfo = { model: string; os: string };

/**
 * 결제 실패 사유. cancelled = 사용자 취소(조용히 종료), pending = 스토어 승인 대기(지급은 나중에 복구 경로로),
 * unavailable = 이 환경·상품으로 결제 불가, failed = 그 외.
 */
export type BillingErrorCode = "cancelled" | "pending" | "unavailable" | "failed";
export type BillingError = { code: BillingErrorCode; cause?: unknown };
export const isBillingError = (e: unknown): e is BillingError =>
  typeof e === "object" && e !== null && "code" in e && typeof (e as BillingError).code === "string";

/** 스토어별 구매 증빙. google → POST /purchases/google, apple → POST /purchases/apple (AC0-179) */
export type PurchaseResult =
  | { store: "google"; productId: string; purchaseToken: string; orderId?: string }
  | { store: "apple"; productId: string; signedTransaction: string; transactionId: string };

/** 기기 스토어가 알려 준 상품 정보(상점·결제 설계 결정 8). priceString = 스토어 현지화 가격 문자열(₩4,900 · ¥500). 모의는 null(서버 가격 표시) */
export type StoreProductInfo = { productId: string; priceString: string | null; currency: string | null };

/**
 * 1.15 결제 포트. 네이티브 = @capgo/native-purchases(Play Billing 8 · StoreKit 2), dev 기본은 모의(`mock:<productId>`, 서버 *_VERIFY=mock).
 * 지급의 진실은 서버: 클라는 증빙만 제출하고, Android consume 은 서버가, iOS finish 는 서버 200 뒤 finish()가 한다.
 * 상점 노출 = 서버 목록 ∩ getProducts(결정 8): 가격은 priceString 우선, 스토어 조회가 실패(throw)하면 서버 가격만 보여 주고 결제는 막는다.
 */
export interface BillingPort {
  /** 스토어 계정 국가(alpha-2). 모르면 null. 네이티브 = getStorefront(iOS alpha-3 → alpha-2), 모의 = KR, 웹 = null */
  getStorefront(): Promise<string | null>;
  /** 기기 스토어의 상품 정보. 스토어에 없는 ID 는 빠진다. 스토어 연결 실패는 throw(호출자가 구매 불가로 처리) */
  getProducts(productIds: string[]): Promise<StoreProductInfo[]>;
  purchase(productId: string): Promise<PurchaseResult>;
  /** 서버가 지급(granted·already_granted)한 뒤 호출. iOS = finishTransaction, Android·모의 = no-op */
  finish(result: PurchaseResult): Promise<void>;
  /** 미처리 구매(강제 종료 복구). 서버가 멱등이라 이미 지급된 증빙이 섞여도 된다. 모의·스텁은 빈 배열. */
  getPendingPurchases(): Promise<PurchaseResult[]>;
  /** 이전 실행·앱 밖에서 끝난 거래(iOS Transaction.updates). 해제 함수를 돌려준다. Android·모의는 호출되지 않는다. */
  onPurchaseUpdated(cb: (result: PurchaseResult) => void): () => void;
}

/** 모든 Amplitude 이벤트(자동 수집 포함)에 붙는 공통 프로퍼티. 프로젝트 하나에 dev·prod 를 env 로 나눈다(AC0-182). */
export type CommonProps = { env: "dev" | "prod"; platform: ClientKind; app_version: string; bundle_version: string };

/**
 * Amplitude 포트. 런타임당 SDK 하나: 웹 = @amplitude/analytics-browser, 앱 = 자체 Capacitor 플러그인 `Amplitude`(Android·iOS).
 * init 전·키 없음·플러그인 없음이면 전부 no-op. 이벤트 이름·프로퍼티·정규화는 src/analytics 가 맡고, 포트는 전송·세션 신호·인스톨 리퍼러만.
 */
export interface AnalyticsPort {
  /** deviceId 는 amplitudeDeviceId(stage, …) 를 거친 값. 두 번째 호출은 무시 */
  init(opts: { apiKey: string; deviceId: string; commonProps: CommonProps }): Promise<void>;
  /** 이후 모든 이벤트(자동 수집 포함)에 병합할 공통 프로퍼티 교체 */
  setCommonProps(props: CommonProps): void;
  track(event: string, props: Record<string, unknown>): void;
  setUserId(userId: string | null): void;
  setUserProperties(o: { set?: Record<string, unknown>; setOnce?: Record<string, unknown> }): void;
  /** SDK 가 새 세션을 시작할 때마다. 구독 전에 시작된 세션은 보관했다가 구독 즉시 전달한다 */
  onSessionStart(cb: () => void): () => void;
  /** Android Play Install Referrer 원문. 리퍼러 없음·웹·iOS 는 null, 일시 실패(서비스 연결 불가 등)는 throw → 다음 세션에 다시 */
  getInstallReferrer(): Promise<string | null>;
  flush(): Promise<void>;
}

/** OTA 확인 결과: 받을 번들 · 내장 번들로 복귀 · 없음(실패·시간 초과 포함) */
export type OtaUpdate = { version: string; url: string; checksum?: string };
export type OtaCheck = OtaUpdate | "builtin" | null;

/** OTA(AC0-170). 네이티브만 동작, 웹은 no-op. */
export interface UpdaterPort {
  /** 서버에 새 번들이 있는지. timeoutMs 안에 답이 없거나 실패하면 null(부팅을 막지 않는다). */
  check(timeoutMs: number): Promise<OtaCheck>;
  /** 내려받고(진행률 0~100) 바로 적용한다. 성공하면 웹뷰가 다시 로드되어 돌아오지 않는다. */
  apply(update: OtaUpdate, onProgress: (percent: number) => void): Promise<void>;
  /** 내장 번들로 복귀(웹뷰 다시 로드). */
  reset(): Promise<void>;
  /** 지금 실행 중인 OTA 번들 버전. 내장 번들·웹·실패는 null */
  currentBundle(): Promise<string | null>;
}

/** OS 알림 권한. prompt = 아직 묻지 않음(Android 13+ 의 다시 물을 수 있는 거부 포함), denied = 다시 물어도 OS 가 창을 띄우지 않음 */
export type PushPermission = "granted" | "denied" | "prompt";

/** 푸시(AC0-260). 네이티브 = @capacitor/push-notifications(FCM), 웹은 없음(늘 denied) */
export interface PushPort {
  permission(): Promise<PushPermission>;
  /** OS 권한 창(F01-16). Android 12 이하는 창 없이 granted */
  requestPermission(): Promise<PushPermission>;
  /** FCM 등록 토큰. 권한이 있어야 한다. 실패·시간 초과·iOS Firebase 미구성(APNs 원본 토큰) = null */
  register(): Promise<string | null>;
  /** 알림 탭(앱이 꺼져 있던 경우 포함 — 플러그인이 보관했다 구독 즉시 전달). data = 서버 FCM data(문자열만). 해제 함수를 돌려준다 */
  onOpened(cb: (data: Record<string, string>) => void): () => void;
}

export interface Platform {
  /** X-Client 헤더 값. 비밀이 아니며 토큰 전달 방식(Bearer/Cookie)만 고른다. */
  clientKind: ClientKind;
  device: DevicePort;
  auth: AuthPort;
  browser: BrowserPort;
  app: AppPort;
  billing: BillingPort;
  analytics: AnalyticsPort;
  updater: UpdaterPort;
  push: PushPort;
}

export const SECURE_KEY_REFRESH_TOKEN = "refreshToken";
